import React from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { Team } from "../../../types/OrganizationTypes";

interface TeamsListProps {
  teams: Team[];
  onDeleteTeam: (id: number) => void;
  onEditTeam?: (team: Team) => void;
}

const TeamsList: React.FC<TeamsListProps> = ({
  teams,
  onDeleteTeam,
  onEditTeam,
}) => {
  const theme = useTheme();

  if (teams.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 1,
        }}
      >
        <GroupIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        <Typography
          variant="subtitle2"
          fontWeight={500}
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          No teams yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Create teams to enable cost allocation and access scoping.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0}>
      {teams.map((team, index) => (
        <Box
          key={team.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1.5,
            px: 2,
            transition: "background 150ms ease",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
            borderBottom:
              index < teams.length - 1
                ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                : "none",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {team.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {team.accessLevel ?? "Read only"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip
              label={`${team.memberCount ?? 0} member${(team.memberCount ?? 0) !== 1 ? "s" : ""}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.7rem" }}
            />
            <Chip
              label={`${team.projectCount ?? 0} project${(team.projectCount ?? 0) !== 1 ? "s" : ""}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.7rem" }}
            />
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {onEditTeam && (
              <IconButton
                size="small"
                onClick={() => onEditTeam(team)}
                sx={{
                  color: "text.disabled",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => onDeleteTeam(team.id)}
              sx={{
                color: "text.disabled",
                "&:hover": { color: "error.main" },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default TeamsList;
